/**
 * "What happens next with this record" - the marker, and the dialog that fixes it.
 *
 * Shared by leads and clients, because they are the same record at two ages: a
 * conversion moves the activities across, so a step planned on a juhuklient is
 * still the step planned on the client it becomes. Two implementations would
 * mean the marker changed meaning at the moment the relationship became real.
 *
 * ── The marker is clickable, and that is the point ───────────────────────────
 *
 * A marker whose fix is three screens away is a complaint with no remedy. The
 * chip opens the dialog that plans the step it is complaining about, so the
 * shortest path from noticing to fixing is one click.
 */
import { useState } from "react";
import { CalendarClock, CalendarPlus, Loader2 } from "lucide-react";
import { DateTimePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  daysUntil,
  defaultStepAt,
  nextStepState,
  type NextStepState,
} from "../../../lib/nextStep";
import { activityTypeLabel } from "../../../lib/activityType";
import type { Activity } from "../../../lib/clients";
import { Btn, Modal } from "../../ui";

/** The kinds a planned step can be. Deliberately short - the point is the DATE,
 *  and a long menu turns a one-click plan into a form. */
const STEP_TYPES = ["call", "meeting", "email", "note"] as const;
type StepType = (typeof STEP_TYPES)[number];

/** The shortcuts, in the order somebody reaches for them. */
const QUICK_DAYS = [1, 3, 7, 14] as const;

/**
 * The chip a list row or a detail header wears.
 *
 * Draws NOTHING when a step is planned. That is the whole design: a badge on
 * every healthy record makes a list where the records needing somebody are the
 * only ones without a badge, which is exactly backwards.
 */
export function NextStepBadge({
  nextStepAt,
  onPlan,
  className,
}: {
  nextStepAt: string | null | undefined;
  /** When given, the chip is a button that opens the planning dialog. */
  onPlan?: () => void;
  className?: string;
}) {
  const t = useT();
  const state: NextStepState = nextStepState(nextStepAt);
  if (state === "planned") return null;

  const late = state === "overdue";
  const label = late
    ? t("nextStep.lateBy", { days: Math.abs(daysUntil(nextStepAt!)) })
    : t("nextStep.missing");

  const body = (
    <>
      <CalendarClock className="size-2.5" />
      {label}
    </>
  );
  const tone = late
    ? "border-destructive/30 bg-destructive/10 text-destructive"
    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400";
  const shape = `inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${tone} ${className ?? ""}`;

  if (!onPlan) return <span className={shape}>{body}</span>;
  return (
    <button
      type="button"
      className={`${shape} relative z-[1] hover:brightness-95`}
      title={t("nextStep.plan")}
      onClick={(e) => {
        // The board card and the list row both wrap the whole row in a link;
        // without this the click navigates instead of opening the dialog.
        e.preventDefault();
        e.stopPropagation();
        onPlan();
      }}
    >
      {body}
    </button>
  );
}

/**
 * Planning one step.
 *
 * The date is prefilled to a week out at nine in the morning, and four shortcuts
 * move it. A blank date field would make the commonest action - "ring them back
 * in a few days" - a typing exercise, and the whole reason the column sat unused
 * for so long is that nothing made planning cheap.
 *
 * ── Two ways in, and the dialog knows neither of them ────────────────────────
 *
 * A record-bound caller (a lead row, a client header) starts from WHO and lets
 * the dialog choose the day. The calendar starts from a DAY and has to be told
 * who. Both are served without the dialog learning which it is on: `initialAt`
 * moves the prefill, `children` is where the caller draws the field the dialog
 * has no business owning, and `canSave` is how that field says it is not ready.
 * The alternative - a `party` prop and a picker in here - would put a client
 * search inside a component whose whole contract is that it does not know
 * whether it is writing to a client or a lead.
 */
export function NextStepDialog({
  title,
  onClose,
  onSave,
  onSaved,
  initialAt,
  canSave = true,
  children,
}: {
  /** The record's name, so the dialog says what it is about. */
  title: string;
  onClose: () => void;
  /** Files the entry. Given by the caller, because a lead and a client post to
   *  different routes and the dialog should not know which it is on. */
  onSave: (body: { type: string; body?: string; dueAt: string }) => Promise<Activity>;
  onSaved: (activity: Activity) => void;
  /**
   * An ISO datetime to open on, instead of a week out.
   *
   * For the caller that already knows the day: the calendar opens this dialog
   * from a day cell, and prefilling anything else would make the operator undo
   * the one decision they had already made by clicking.
   */
  initialAt?: string;
  /**
   * False while the caller's own field is still empty.
   *
   * The three record-bound callers always know who the step is about, so the
   * default is true. The calendar does not - it starts from a DAY - and a
   * submit button that is live before a party is chosen would post an entry
   * with nowhere to file it.
   */
  canSave?: boolean;
  /**
   * The caller's own field, drawn above the type chips.
   *
   * A slot rather than a `party` prop, because the dialog must go on not
   * knowing which table it is writing to; that is the same reason `onSave` is
   * injected. Whoever fills this slot owns the state behind it and flips
   * `canSave`.
   */
  children?: React.ReactNode;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [type, setType] = useState<StepType>("call");
  const [note, setNote] = useState("");
  // The state IS the ISO string the route wants. It used to be a
  // `datetime-local` value converted on submit, which meant the field's format
  // and the API's format were two different things one helper apart.
  const [when, setWhen] = useState(() => initialAt ?? defaultStepAt(7));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // `DateTimePicker` is clearable, so an empty box is reachable and must not
  // post. Anything non-empty came out of the picker and is already valid.
  const iso = when || null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!iso || !canSave) return;
    setBusy(true);
    setFailed(false);
    try {
      const created = await onSave({ type, dueAt: iso, ...(note.trim() ? { body: note.trim() } : {}) });
      onSaved(created);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("nextStep.plan")} label={t("nextStep.plan")} onClose={onClose} className="max-w-[460px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <p className="crm-sub">{title}</p>

        {children}

        <div>
          <span className="crm-lab">{t("nextStep.what")}</span>
          <div className="flex flex-wrap gap-1.5">
            {STEP_TYPES.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={type === k}
                onClick={() => setType(k)}
                // `crm-cb` on its own is the 38x38 ICON circle - `width: 38px;
                // height: 38px; display: grid`. Without `crm-cb-wide` every one
                // of these labels was crushed into that circle and wrapped
                // mid-word ("E-kiri" over two lines). The pairing is what
                // `ClientsList` already uses for a chip with words in it.
                className={`crm-cb crm-cb-wide${type === k ? " is-on" : ""}`}
              >
                {/* The same labels the timeline draws these entries with, so a
                    step planned as a "call" is not filed under one word and read
                    back under another. */}
                {activityTypeLabel(k, t)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="crm-lab">{t("nextStep.when")}</span>
          {/* The platform's own picker, not `<input type="datetime-local">`.
              The native control looks different in every browser, hides its
              month behind an OS widget, and offers no way to reach a year that
              is not the next one along. `withYearPicker` turns the caption into
              month + year dropdowns; the arrows either side still step a month
              at a time. */}
          {/* No `placeholder`: the label above already says when, and the
              picker's own empty text tells the operator what to DO, which is
              the more useful of the two. */}
          <DateTimePicker value={when} onChange={setWhen} locale={locale} withYearPicker />
          {/* The shortcuts, because the commonest plan is "in a few days" and a
              bare date field turns that into typing. */}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {QUICK_DAYS.map((d) => (
              <button
                key={d}
                type="button"
                // Same pairing as the type chips above, and the same reason: a
                // three-word shortcut label does not fit in a 38px circle.
                className="crm-cb crm-cb-wide"
                onClick={() => setWhen(defaultStepAt(d))}
              >
                {t("nextStep.inDays", { days: d })}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="crm-lab">{t("nextStep.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("nextStep.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        {failed ? <p className="text-[11.5px] text-destructive">{t("common.error")}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !iso || !canSave}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <CalendarPlus className="size-4" />}
            {t("nextStep.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
